import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';
import { getApiBaseUrl } from '../../../shared/constants/api';
import { parseContentDispositionFilename } from '../../../shared/utils/fileDownload';

/**
 * Where the client endpoints live: the API base plus `external/`.
 *
 * Joined rather than concatenated because the base comes with or without a
 * trailing slash depending on where it was set: the .env files end in one, the
 * production and staging builds do not. Plain concatenation would turn the
 * latter into `.../apiexternal/`.
 */
export const externalApiUrl = (apiBase = getApiBaseUrl()) =>
  `${String(apiBase).replace(/\/+$/, '')}/external/`;

// The cached responses an event's own fields live in: each page of the list that
// holds it, and its details. Both tag themselves with the event's id.
const EVENT_CACHES = ['getClientEvents', 'getClientEventDetail'];

/** The copies of one event in a cached response: a page of the list, or its details. */
const eventsIn = (draft, eventId) =>
  (Array.isArray(draft?.results) ? draft.results : [draft]).filter(
    (event) => String(event?.id) === String(eventId)
  );

/**
 * Client Events API Slice
 * Handles all event-related API calls for the client portal
 * Events are automatically filtered by user's assigned groups on the backend
 */
export const clientEventsApiSlice = createApi({
  reducerPath: 'clientEventsApi',
  baseQuery: baseQueryWithReauth(externalApiUrl()),
  tagTypes: ['ClientEvent', 'ClientEventStats', 'ClientEventComment'],
  endpoints: (builder) => ({
    // Get list of events for client's groups. The params arrive already in the
    // API's snake_case from clientEventsQuery.js, the one place filters are
    // translated, so the grid, the export count and the export cannot drift.
    getClientEvents: builder.query({
      query: (params = {}) => ({
        url: 'client/events/',
        params,
      }),
      providesTags: (result) => [
        { type: 'ClientEvent', id: 'LIST' },
        ...(result?.results ?? []).map((event) => ({ type: 'ClientEvent', id: event.id })),
      ],
      keepUnusedDataFor: 30, // Cache for 30 seconds
    }),

    // How many events a set of filters matches, for the export dialog. Asks the
    // list for a single row so the count costs one query, not a page of events.
    getClientEventCount: builder.query({
      query: (params = {}) => ({
        url: 'client/events/',
        params: { ...params, page: 1, page_size: 1 },
      }),
      transformResponse: (response) => ({
        count: typeof response?.count === 'number' ? response.count : null,
      }),
      providesTags: [{ type: 'ClientEvent', id: 'LIST' }],
    }),

    // Get single event details
    getClientEventDetail: builder.query({
      query: (eventId) => `client/events/${eventId}/`,
      providesTags: (result, error, eventId) => [{ type: 'ClientEvent', id: eventId }],
      keepUnusedDataFor: 60, // Cache for 1 minute - event details change less frequently
    }),

    // Get event statistics
    getClientEventStats: builder.query({
      query: () => 'client/events/stats/',
      providesTags: ['ClientEventStats'],
      keepUnusedDataFor: 60, // Cache for 1 minute - stats are aggregated
    }),

    // The notes clients have written on an event, oldest first.
    getClientEventComments: builder.query({
      query: (eventId) => `client/events/${eventId}/comments/`,
      providesTags: (result, error, eventId) => [{ type: 'ClientEventComment', id: eventId }],
    }),

    // Add a note to an event. It refreshes that event's notes, so the new one shows at
    // once, and nothing else: a note changes neither the event nor the list.
    addClientEventComment: builder.mutation({
      query: ({ eventId, comment }) => ({
        url: `client/events/${eventId}/comments/`,
        method: 'POST',
        body: { comment },
      }),
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'ClientEventComment', id: eventId },
      ],
    }),

    // Set, change or clear the client's own reference for an event: one per event,
    // and "" clears it. The server answers with what it saved, and who saved it when.
    setClientEventReference: builder.mutation({
      query: ({ eventId, reference }) => ({
        url: `client/events/${eventId}/reference/`,
        method: 'PUT',
        body: { reference },
      }),
      // Only the fields a reference sets on an event, so they can be laid straight
      // over a cached event or a grid row.
      transformResponse: (response) => ({
        reference: response?.reference ?? '',
        reference_set_by: response?.reference_set_by ?? '',
        reference_set_at: response?.reference_set_at ?? null,
      }),
      // Invalidates nothing when it saves: that would refetch the whole page of events,
      // and the grid would show its spinner after every edit. The cached pages that hold
      // the event, and its cached details, are patched instead: at once with what was
      // typed, then with what the server saved.
      async onQueryStarted({ eventId, reference }, { dispatch, getState, queryFulfilled }) {
        const tag = { type: 'ClientEvent', id: eventId };
        const patchCachedEvent = (recipe) =>
          clientEventsApiSlice.util
            .selectInvalidatedBy(getState(), [tag])
            .filter(({ endpointName }) => EVENT_CACHES.includes(endpointName))
            .forEach(({ endpointName, originalArgs }) =>
              dispatch(
                clientEventsApiSlice.util.updateQueryData(endpointName, originalArgs, (draft) =>
                  eventsIn(draft, eventId).forEach(recipe)
                )
              )
            );

        let shownBefore = '';
        patchCachedEvent((event) => {
          shownBefore = event.reference ?? '';
          event.reference = reference;
        });
        try {
          const { data } = await queryFulfilled;
          patchCachedEvent((event) => Object.assign(event, data));
        } catch {
          // Refused, or never answered. Undoing the patch above would put back the cache
          // as it was when this save began, place by place, over a later save of the same
          // event, or over another event on a page fetched again since. So the event is
          // put back by its id, only where it still shows what this save typed, and the
          // server is asked what it really holds.
          patchCachedEvent((event) => {
            if (event.reference === reference) event.reference = shownBefore;
          });
          dispatch(clientEventsApiSlice.util.invalidateTags([tag]));
        }
      },
    }),

    // Assign or update driver on an event
    assignEventDriver: builder.mutation({
      query: ({ eventId, driverId }) => ({
        url: `client/events/${eventId}/assign-driver/`,
        method: 'PATCH',
        body: { driver_id: driverId },
      }),
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'ClientEvent', id: eventId },
        'ClientEvent',
        'ClientEventStats',
      ],
    }),

    // Remove driver from an event
    removeEventDriver: builder.mutation({
      query: ({ eventId }) => ({
        url: `client/events/${eventId}/assign-driver/`,
        method: 'PATCH',
        body: { driver_id: null },
      }),
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'ClientEvent', id: eventId },
        'ClientEvent',
        'ClientEventStats',
      ],
    }),

    // What the export can write: formats, the column catalogue and the row caps.
    getClientEventsExportCatalogue: builder.query({
      query: () => 'client/events/export/',
      keepUnusedDataFor: 600,
    }),

    // Export events. Filters travel in the query string under the list's own
    // names; the body carries only the format and the columns.
    exportClientEvents: builder.mutation({
      query: ({ params = {}, body }) => ({
        url: 'client/events/export/',
        method: 'POST',
        params,
        body,
        // NOT the string 'blob': RTK Query's handler union is
        // 'content-type' | 'json' | 'text' | function, and anything else falls
        // through to response.text(). A CSV would survive that; an .xlsx or a
        // .pdf would be decoded as UTF-8 and arrive corrupt. Errors stay text
        // so the dialog can show the server's reason.
        responseHandler: (response) => (response.ok ? response.blob() : response.text()),
      }),
      // meta carries the raw Response, the only place the filename is readable.
      transformResponse: (blob, meta) => ({
        blob,
        filename: parseContentDispositionFilename(
          meta?.response?.headers?.get('content-disposition')
        ),
      }),
      // A read that happens to be a POST: it must not churn the event cache.
    }),
  }),
});

// Export hooks for components to use
export const {
  useGetClientEventsQuery,
  useGetClientEventCountQuery,
  useGetClientEventDetailQuery,
  useGetClientEventStatsQuery,
  useGetClientEventCommentsQuery,
  useAddClientEventCommentMutation,
  useSetClientEventReferenceMutation,
  useAssignEventDriverMutation,
  useRemoveEventDriverMutation,
  useGetClientEventsExportCatalogueQuery,
  useExportClientEventsMutation,
} = clientEventsApiSlice;
